import { API_BASE_URL } from '../../api/constants.js';

/**
 * LoginApi — dedicated Symfony API client for the login page. Deliberately
 * does NOT extend the shared `BaseApi`/`apiRequest` (`api/utils.js`): that
 * client hard-navigates to `/authorization/login/` on any 401
 * (`api/authSession.js`) — on THIS page, a wrong password IS a 401 and must
 * render inline, not bounce the visitor off the form they're looking at.
 *
 * `credentials: 'include'` on every call is what lets the backend's
 * `hs_login_handoff` cookie (set only on a real browser request whose
 * Origin/Referer is a brand domain) actually land in the browser's cookie
 * jar — that cookie, not anything this client does, is what lets legacy
 * establish its own session on the next navigation.
 */
async function request(path, { method = 'GET', body, headers = {} } = {}) {
	const response = await fetch(`${API_BASE_URL}${path}`, {
		method,
		credentials: 'include',
		headers: { 'Content-Type': 'application/json', ...headers },
		...(body !== undefined && { body: JSON.stringify(body) })
	});

	let data = null;
	try {
		data = await response.json();
	} catch {
		data = null;
	}

	if (!response.ok) {
		const error = new Error(`HTTP ${response.status}: ${response.statusText}`);
		error.response = { data, status: response.status };
		throw error;
	}

	return { data };
}

class LoginApi {
	/**
	 * POST /api/v1/auth/login — 200 with `{accessToken, refreshToken, user}` on
	 * success (the hand-off cookie rides on this same response). 401
	 * `invalid_credentials` or `account_not_activated`, 429 rate-limited (flat
	 * body, no code), 422 validation.
	 */
	static login(email, password) {
		return request('/auth/login', { method: 'POST', body: { email, password, device: 'web-legacy' } });
	}

	/**
	 * POST /api/v1/auth/oauth/{provider} — same success shape as login. 401
	 * (bad token, or a matched-but-ineligible account — the latter DOES carry
	 * `data.code`), 409 no account found for this email, 429 rate-limited.
	 */
	static oauthLogin(provider, idToken) {
		return request(`/auth/oauth/${provider}`, { method: 'POST', body: { idToken, device: 'web-legacy' } });
	}

	/**
	 * GET /api/v1/user/profile — the one call this page makes AFTER a
	 * successful login to learn whether the account is registration-incomplete.
	 * `registration_incomplete` never comes back from /login or /oauth
	 * directly (that gate only runs on protected routes) — this IS the first
	 * protected call. 401 here means the just-issued token is already invalid
	 * (should not happen in practice).
	 */
	static getProfile(accessToken) {
		return request('/user/profile', { headers: { Authorization: `Bearer ${accessToken}` } });
	}

	/**
	 * POST /api/v1/auth/register/resend — offered inline when login fails with
	 * `account_not_activated`. Always 200 (enumeration-safe), 429 past 5/hour.
	 */
	static resendConfirmation(email) {
		return request('/auth/register/resend', { method: 'POST', body: { email } });
	}
}

export default LoginApi;
